import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import hrOvertimeService from '../services/hrOvertimeService';
import staffService from '../services/staffService';
import payrollService from '../services/payrollService';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, XCircle, PaperPlaneTilt, CurrencyCircleDollar } from '@phosphor-icons/react';

const ADMIN_ROLES = ['super_admin', 'school_admin', 'hr'];

const STATUS_COLORS = {
  pending: '#d97706',
  approved: '#16a34a',
  rejected: '#dc2626',
  paid: '#2563eb',
};

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function StatusBadge({ status }) {
  return (
    <span className="capitalize" style={{ color: STATUS_COLORS[status] || '#374151', fontWeight: 600 }}>
      {status}
    </span>
  );
}

function HROvertimePage() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  const [myStaffId, setMyStaffId] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [myRecords, setMyRecords] = useState([]);
  const [loadingMy, setLoadingMy] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ work_date: todayISO(), hours: '', rate_multiplier: 1.5, reason: '' });

  const [adminView, setAdminView] = useState('pending'); // pending | all
  const [adminRecords, setAdminRecords] = useState([]);
  const [loadingAdmin, setLoadingAdmin] = useState(false);

  const [payrollRuns, setPayrollRuns] = useState([]);
  const [selectedRunByRecord, setSelectedRunByRecord] = useState({});
  const [pushingId, setPushingId] = useState(null);

  const loadMy = useCallback(() => {
    if (!myStaffId) {
      setMyRecords([]);
      setLoadingMy(false);
      return;
    }
    setLoadingMy(true);
    hrOvertimeService.list({ staff_id: myStaffId })
      .then((data) => setMyRecords(Array.isArray(data) ? data : []))
      .catch((err) => toast.error(err.message || 'Failed to load your overtime records'))
      .finally(() => setLoadingMy(false));
  }, [myStaffId]);

  const loadAdmin = useCallback(() => {
    if (!isAdmin) return;
    setLoadingAdmin(true);
    hrOvertimeService.list(adminView === 'pending' ? { status: 'pending' } : {})
      .then((data) => setAdminRecords(Array.isArray(data) ? data : []))
      .catch((err) => toast.error(err.message || 'Failed to load overtime records'))
      .finally(() => setLoadingAdmin(false));
  }, [isAdmin, adminView]);

  useEffect(() => {
    setLoadingProfile(true);
    staffService.getMyProfile()
      .then((data) => setMyStaffId(data?.staff_id || null))
      .catch(() => setMyStaffId(null))
      .finally(() => setLoadingProfile(false));
  }, []);

  useEffect(() => { loadMy(); }, [loadMy]);
  useEffect(() => { loadAdmin(); }, [loadAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    payrollService.listRuns(100, 0)
      .then((data) => setPayrollRuns(data?.items || data?.records || (Array.isArray(data) ? data : [])))
      .catch(() => {});
  }, [isAdmin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!myStaffId) {
      toast.error('No staff profile linked to your account — cannot claim overtime.');
      return;
    }
    try {
      setSubmitting(true);
      await hrOvertimeService.create({
        staff_id: myStaffId,
        work_date: form.work_date,
        hours: Number(form.hours),
        rate_multiplier: Number(form.rate_multiplier) || 1.5,
        reason: form.reason || undefined,
      });
      toast.success('Overtime claim submitted');
      setForm({ work_date: todayISO(), hours: '', rate_multiplier: 1.5, reason: '' });
      loadMy();
      if (isAdmin) loadAdmin();
    } catch (error) {
      toast.error(error.message || 'Unable to submit overtime claim');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await hrOvertimeService.approve(id);
      toast.success('Overtime record approved');
      loadAdmin();
    } catch (error) {
      toast.error(error.message || 'Unable to approve overtime record');
    }
  };

  const handleReject = async (id) => {
    try {
      await hrOvertimeService.reject(id, '');
      toast.success('Overtime record rejected');
      loadAdmin();
    } catch (error) {
      toast.error(error.message || 'Unable to reject overtime record');
    }
  };

  const handlePush = async (id) => {
    const runId = selectedRunByRecord[id];
    if (!runId) {
      toast.error('Choose a payroll run first');
      return;
    }
    setPushingId(id);
    try {
      await hrOvertimeService.pushToPayroll(id, runId);
      toast.success('Overtime pushed to payroll as a pending adjustment');
      loadAdmin();
    } catch (error) {
      toast.error(error.message || 'Unable to push overtime to payroll');
    } finally {
      setPushingId(null);
    }
  };

  const renderRecordsTable = (records, { showStaff, adminActions }) => (
    <table className="data-table">
      <thead>
        <tr>
          {showStaff && <th>Staff</th>}
          <th>Date</th>
          <th>Hours</th>
          <th>Rate</th>
          <th>Reason</th>
          <th>Status</th>
          {adminActions && <th>Actions</th>}
        </tr>
      </thead>
      <tbody>
        {records.map((r) => (
          <tr key={r.id}>
            {showStaff && <td>{r.staff_id}</td>}
            <td>{r.work_date}</td>
            <td>{r.hours}</td>
            <td>{r.rate_multiplier}x</td>
            <td>{r.reason || '-'}</td>
            <td><StatusBadge status={r.status} /></td>
            {adminActions && (
              <td>
                {r.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm btn-success" onClick={() => handleApprove(r.id)} title="Approve">
                      <CheckCircle size={14} />
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleReject(r.id)} title="Reject">
                      <XCircle size={14} />
                    </button>
                  </div>
                )}
                {r.status === 'approved' && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <select
                      value={selectedRunByRecord[r.id] || ''}
                      onChange={(e) => setSelectedRunByRecord((prev) => ({ ...prev, [r.id]: e.target.value }))}
                      style={{ padding: 4, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 }}
                    >
                      <option value="">Select payroll run...</option>
                      {payrollRuns.map((run) => (
                        <option key={run.id} value={run.id}>{run.period_name} ({run.status})</option>
                      ))}
                    </select>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handlePush(r.id)}
                      disabled={pushingId === r.id}
                      title="Push to payroll"
                    >
                      <CurrencyCircleDollar size={14} /> {pushingId === r.id ? 'Pushing...' : 'Push'}
                    </button>
                  </div>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Overtime</h1>
            <p>Claim and track overtime hours. {isAdmin && 'Approve or reject claims from your staff below.'}</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) 2fr', gap: 24, alignItems: 'start' }}>
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ marginTop: 0 }}>Claim overtime</h3>
            {loadingProfile ? (
              <div className="loading">Loading...</div>
            ) : !myStaffId ? (
              <p style={{ fontSize: 13, color: '#6b7280' }}>No staff profile linked to your account — you cannot submit overtime claims.</p>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
                <label style={{ fontSize: 13 }}>
                  Work date
                  <input
                    type="date"
                    required
                    value={form.work_date}
                    onChange={(e) => setForm({ ...form, work_date: e.target.value })}
                    style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }}
                  />
                </label>
                <label style={{ fontSize: 13 }}>
                  Hours
                  <input
                    type="number"
                    min="0.5"
                    max="24"
                    step="0.5"
                    required
                    value={form.hours}
                    onChange={(e) => setForm({ ...form, hours: e.target.value })}
                    style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }}
                  />
                </label>
                <label style={{ fontSize: 13 }}>
                  Rate multiplier
                  <input
                    type="number"
                    min="1"
                    step="0.1"
                    value={form.rate_multiplier}
                    onChange={(e) => setForm({ ...form, rate_multiplier: e.target.value })}
                    style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }}
                  />
                </label>
                <label style={{ fontSize: 13 }}>
                  Reason (optional)
                  <textarea
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    rows={3}
                    style={{ width: '100%', padding: 8, marginTop: 4, borderRadius: 6, border: '1px solid #d1d5db' }}
                  />
                </label>
                <button className="btn btn-primary" type="submit" disabled={submitting}>
                  <PaperPlaneTilt size={16} /> {submitting ? 'Submitting...' : 'Submit claim'}
                </button>
              </form>
            )}
          </div>

          <div>
            <div className="card" style={{ padding: 20, marginBottom: 20 }}>
              <h3 style={{ marginTop: 0 }}>Your overtime</h3>
              {loadingMy ? (
                <div className="loading">Loading...</div>
              ) : myRecords.length === 0 ? (
                <p style={{ fontSize: 13, color: '#6b7280' }}>No overtime records yet.</p>
              ) : (
                renderRecordsTable(myRecords, { showStaff: false, adminActions: false })
              )}
            </div>

            {isAdmin && (
              <div className="card" style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ marginTop: 0 }}>Staff overtime records</h3>
                </div>
                <div className="view-tabs" style={{ marginBottom: 12 }}>
                  <button className={`tab ${adminView === 'pending' ? 'active' : ''}`} onClick={() => setAdminView('pending')}>Pending</button>
                  <button className={`tab ${adminView === 'all' ? 'active' : ''}`} onClick={() => setAdminView('all')}>All</button>
                </div>
                {loadingAdmin ? (
                  <div className="loading">Loading...</div>
                ) : adminRecords.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#6b7280' }}>No overtime records to show.</p>
                ) : (
                  renderRecordsTable(adminRecords, { showStaff: true, adminActions: true })
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default HROvertimePage;
